import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LogIn } from 'lucide-react'
import { AuthShell } from './AuthShell'
import { Button, ErrorNote, Field, Input } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { authErrorMessage } from '@/lib/authErrors'
import { useFeatures } from '@/lib/features'

export function SignInPage() {
  const { signIn } = useAuth()
  const { features } = useFeatures()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Kom användaren hit från en skyddad sida skickas hen tillbaka dit.
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/'

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await signIn(email, password)
      navigate(from, { replace: true })
    } catch (caught) {
      setError(authErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell
      title="Välkommen tillbaka"
      showValue
      footer={
        /* Innan lanseringen finns ingenting att skapa – då är raden bara en
           väg till en spärrad sida. */
        features.signUp ? (
          <>
            Har du inget konto?{' '}
            <Link
              to="/skapa-konto"
              className="font-semibold text-accent underline underline-offset-2"
            >
              Skapa ett
            </Link>
          </>
        ) : null
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && <ErrorNote>{error}</ErrorNote>}

        <Field label="E-post" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="namn@exempel.se"
            />
          )}
        </Field>

        <Field label="Lösenord" required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>

        <Button type="submit" size="lg" fullWidth loading={busy} icon={<LogIn size={18} />}>
          Logga in
        </Button>

        <p className="text-center text-sm">
          <Link to="/glomt-losenord" className="text-muted underline underline-offset-2">
            Glömt lösenordet?
          </Link>
        </p>
      </form>
    </AuthShell>
  )
}
